import { Link, useNavigate } from "react-router-dom";
import { PlusCircle, Leaf, Trophy, CalendarClock, ChevronRight, BookOpen, Newspaper } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Card, BigButton } from "@/components/Layout";
import { StatusBadge, StatusSteps } from "@/components/Status";
import { PartnerCard } from "@/components/PartnerCard";
import { fmtDate } from "@/lib/api";
import { FactCard } from "@/components/FactCard";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

export default function Home() {
  const { user, summary, t, lang } = useApp();
  const nav = useNavigate();
  const first = user ? (user.contact_person || user.full_name || user.name || "").split(" ")[0] : "";
  const nx = summary?.next_pickup;
  const imp = summary?.impact;

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm font-semibold text-[#2E9E6B]">{t(greeting())}{first ? "," : "!"}</p>
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-[#0D3B2B]" data-testid="home-greeting">{first || t("Welcome to Kindsaver")}</h1>
      </div>

      <div className="ks-grain relative overflow-hidden rounded-3xl bg-[#0D3B2B] p-6 text-white">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#2E9E6B]/30" />
        <p className="relative font-display text-2xl font-bold leading-snug">{t("Have scrap at home or work?")}</p>
        <p className="relative mb-5 mt-1 text-sm text-white/70">{t("We'll come and collect it. It takes less than a minute to book.")}</p>
        <BigButton coral onClick={() => nav("/book")} data-testid="home-book-pickup-btn" className="relative h-16 text-lg">
          <PlusCircle className="h-6 w-6" /> {t("Book a pickup")}
        </BigButton>
      </div>

      {!user && (
        <Card className="space-y-2" data-testid="home-guest-card">
          <p className="text-[#4A6358]">{t("No login needed to start. You'll log in with Google or your mobile number only at the last step.")}</p>
          <Link to="/login" className="font-semibold text-[#2E9E6B]" data-testid="home-login-link">{t("Already booked with us? Log in")}</Link>
        </Card>
      )}
      {user && (<>
      <Card data-testid="home-next-pickup">
        <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#7B968B]">
          <CalendarClock className="h-4 w-4" /> {t("Next pickup")}
        </div>
        {nx ? (
          <div className="space-y-3">
          <Link to={`/history/${nx.booking_id}`} className="block space-y-3" data-testid="home-next-pickup-link">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-display text-xl font-bold text-[#0D3B2B]">{fmtDate(nx.date, lang)} · {nx.slot_label}</p>
                <p className="text-sm text-[#4A6358]">{nx.scrap_types.map(t).join(", ")}</p>
              </div>
              <StatusBadge status={nx.status} testid="home-next-status" />
            </div>
            <StatusSteps status={nx.status} />
          </Link>
          <PartnerCard staff={nx.staff} status={nx.status} trip={nx.trip_status} />
          </div>
        ) : (
          <p className="text-[#4A6358]" data-testid="home-no-pickup">{t("No upcoming pickups. Book one whenever you are ready.")}</p>
        )}
      </Card>

      <Link to="/impact" data-testid="home-impact-card" className="block">
        <Card className="flex items-center gap-4 transition-shadow hover:shadow-md">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#2E9E6B]/15"><Leaf className="h-7 w-7 text-[#2E9E6B]" /></div>
          <div className="flex-1">
            <p className="font-display text-2xl font-extrabold text-[#0D3B2B]"><span data-testid="home-total-kg">{imp?.total_kg ?? 0}</span> kg</p>
            <p className="text-sm text-[#4A6358]">{t("recycled")} · {imp?.completed ?? 0} {t("pickups done")}</p>
          </div>
          <div className="text-right">
            <Trophy className="ml-auto h-5 w-5 text-[#E05C3A]" />
            <p className="font-display text-lg font-bold text-[#0D3B2B]" data-testid="home-rank">#{summary?.rank ?? "–"}</p>
          </div>
          <ChevronRight className="h-5 w-5 text-[#7B968B]" />
        </Card>
      </Link>
      <FactCard />
      <Link to="/rates" data-testid="home-rates-link" className="flex items-center justify-between rounded-3xl bg-[#0D3B2B] p-4 text-white">
        <span><span className="block font-bold">{t("Today's scrap rates")}</span><span className="text-xs text-white/75">{t("Live price per kg, same for everyone")}</span></span>
        <span className="rounded-full bg-[#E05C3A] px-3 py-1 text-xs font-bold">{t("View")}</span>
      </Link>
      <div className="grid grid-cols-2 gap-3">
        <Link to="/learn" data-testid="home-learn-card"><Card className="h-full space-y-1 p-4 transition-shadow hover:shadow-md"><BookOpen className="h-6 w-6 text-[#2E9E6B]" /><p className="font-bold text-[#0D3B2B]">{t("Pickup guide")}</p><p className="text-xs text-[#4A6358]">{t("Tips + daily quiz for Green Points")}</p></Card></Link>
        <Link to="/news" data-testid="home-news-card"><Card className="h-full space-y-1 p-4 transition-shadow hover:shadow-md"><Newspaper className="h-6 w-6 text-[#E05C3A]" /><p className="font-bold text-[#0D3B2B]">{t("News & rules")}</p><p className="text-xs text-[#4A6358]">{t("Local, India & world updates")}</p></Card></Link>
      </div>
      <p className="px-1 text-sm text-[#4A6358]">
        {imp?.total_kg > 0 ? t("Every kilo you recycle keeps Jaipur cleaner. Thank you!") : t("Your first pickup is the start of something great for Jaipur.")}
      </p>
      </>)}
      <p className="flex justify-center gap-4 pt-2 text-xs text-[#7B968B]" data-testid="home-legal-links"><a href="/privacy.html">{t("Privacy Policy")}</a><a href="/terms.html">{t("Terms of Service")}</a></p>
    </div>
  );
}
